import { createColumnHelper } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { LuCheck, LuPlus } from 'react-icons/lu';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { InviteStatus } from '@ndvr/auth-api';
import { CommandCenterHousehold, OnboardingStage } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import { Page, PageContent, PageTitle, PageTitleRow, PageTopBar } from '@/components/ui/page-layout';
import { cn } from '@/utils/cn';
import { AdvisorLoginButton } from 'modules';
import { getHouseholdName, getPrimaryManagerName, toNumber } from 'utils';
import { useSelectedTenantGUIDs } from 'views/auth/tenant-selection';
import { useCommandCenterHouseholds } from 'views/household/household-queries';
import { useInviteDetails, useInvitePerson } from 'views/person/person-queries';
import { ButtonWithConfirmModal, CustomTablePage, ModalText } from 'widgets';
import { GlobalSearchTermInput } from 'widgets/form/filter/GlobalSearchTermInput';

import { CreateOnboardingHouseholdSheet } from './CreateOnboardingHouseholdSheet';
import {
    formatAssets,
    getStageAgeLabel,
    getStageEnteredTime,
    OnboardingRow,
    onboardingStages,
    toOnboardingRows,
} from './onboarding-progress';
import { useOnboardingAccountsSummaries, useOnboardingHouseholds } from './onboarding-queries';

const columnHelper = createColumnHelper<OnboardingRow>();

export const OnboardingListPage = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const stageParam = searchParams.get('stage');
    const selectedStage = Object.values(OnboardingStage).find((stage) => stage === stageParam);
    const columnFilters = useMemo(
        () => (selectedStage ? [{ id: 'stage', value: selectedStage }] : []),
        [selectedStage],
    );
    const tenantGUIDs = useSelectedTenantGUIDs();
    const [creating, setCreating] = useState(false);
    const households = useCommandCenterHouseholds();
    const statuses = useOnboardingHouseholds();
    const summaries = useOnboardingAccountsSummaries();
    const rows = useMemo(
        () => toOnboardingRows(statuses.data ?? [], households.data ?? [], summaries.data ?? []),
        [households.data, statuses.data, summaries.data],
    );
    const columns = useMemo(
        () => [
            columnHelper.accessor((row) => getHouseholdName(row.household), {
                id: 'household',
                header: 'Household',
                cell: ({ row, getValue }) => (
                    <Link
                        className="text-primary font-normal hover:underline"
                        to={`/onboarding/${row.original.globalAccountGUID}`}
                        onClick={(event) => event.stopPropagation()}
                    >
                        {getValue()}
                    </Link>
                ),
            }),
            columnHelper.accessor((row) => getPrimaryManagerName(row.household), {
                id: 'contact',
                header: 'Primary contact',
                cell: ({ row, getValue }) => (
                    <div>
                        <div>{getValue()}</div>
                        <div className="text-muted-foreground text-xs">
                            {row.original.household.primaryManagerEmailAddressPlain}
                        </div>
                    </div>
                ),
            }),
            columnHelper.accessor((row) => row.accounts?.accountCount, {
                id: 'accounts',
                header: 'Accounts',
                sortUndefined: 'last',
                cell: ({ getValue }) => <span className="tabular-nums">{getValue() ?? '—'}</span>,
            }),
            columnHelper.accessor((row) => toNumber(row.accounts?.expectedValue), {
                id: 'expectedAssets',
                header: 'Est. assets',
                sortUndefined: 'last',
                cell: ({ getValue }) => (
                    <span className="tabular-nums" title="The sum of the expected values of the onboarding accounts">
                        {formatAssets(getValue())}
                    </span>
                ),
            }),
            columnHelper.accessor((row) => toNumber(row.accounts?.receivedValue), {
                id: 'receivedAssets',
                header: 'Assets received',
                sortUndefined: 'last',
                cell: ({ getValue }) => (
                    <span className="tabular-nums" title="The latest reported market value of the opened accounts">
                        {formatAssets(getValue())}
                    </span>
                ),
            }),
            columnHelper.accessor('stage', {
                header: 'Stage',
                filterFn: 'equals',
                sortingFn: (left, right) =>
                    Object.keys(onboardingStages).indexOf(left.original.stage) -
                    Object.keys(onboardingStages).indexOf(right.original.stage),
                cell: ({ row }) => (
                    <span
                        className={cn(
                            'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap text-[#2d2524]',
                            onboardingStages[row.original.stage].badgeClass,
                        )}
                    >
                        {onboardingStages[row.original.stage].complete && (
                            <LuCheck className="size-3 shrink-0" aria-hidden="true" />
                        )}
                        {onboardingStages[row.original.stage].label}
                    </span>
                ),
            }),
            columnHelper.accessor((row) => (getStageEnteredTime(row) === undefined ? undefined : row.stageEnteredAt), {
                id: 'stageEnteredAt',
                header: 'Time in stage',
                sortUndefined: 'last',
                sortDescFirst: false,
                sortingFn: (left, right) =>
                    (getStageEnteredTime(left.original) ?? 0) - (getStageEnteredTime(right.original) ?? 0),
                cell: ({ row }) => (
                    <span
                        title={
                            row.original.stageEnteredAt
                                ? `Entered stage ${row.original.stageEnteredAt}.`
                                : 'Stage entry time is unknown'
                        }
                    >
                        {getStageAgeLabel(row.original)}
                    </span>
                ),
            }),
            columnHelper.display({
                id: 'actions',
                header: 'Actions',
                cell: ({ row }) => <OnboardingActions household={row.original.household} />,
            }),
        ],
        [],
    );

    return (
        <Page>
            <PageTopBar />
            <PageTitleRow
                actions={
                    <div className="flex items-center gap-2">
                        <GlobalSearchTermInput placeholder="Filter table ..." round />
                        <Button variant="highlighted" onClick={() => setCreating(true)} disabled={!tenantGUIDs.length}>
                            <LuPlus /> Create Onboarding Household
                        </Button>
                    </div>
                }
            >
                <PageTitle>Onboarding</PageTitle>
            </PageTitleRow>
            <PageContent scroll="children" padding="none">
                {households.isError || statuses.isError ? (
                    <div role="alert" className="p-6">
                        Could not load onboarding households.{' '}
                        <Button
                            variant="outline"
                            onClick={() => {
                                void households.refetch();
                                void statuses.refetch();
                            }}
                        >
                            Retry
                        </Button>
                    </div>
                ) : !tenantGUIDs.length ? (
                    <p className="p-6">Select a client base to view onboarding households.</p>
                ) : (
                    <CustomTablePage
                        columns={columns}
                        columnFilters={columnFilters}
                        data={rows}
                        isLoading={households.isLoading || statuses.isLoading}
                        defaultSortCol="stageEnteredAt"
                        defaultSortDirection="asc"
                        toolbarPosition="header"
                        renderToolbar={(table) => {
                            const stage = table.getColumn('stage')!.getFilterValue() ?? 'all';
                            const matchingRows = table.getColumn('stage')!.getFacetedRowModel().rows;
                            const stageFilters = [
                                { value: 'all' as const, label: 'All', count: matchingRows.length },
                                ...(Object.keys(onboardingStages) as OnboardingStage[]).map((value) => ({
                                    value,
                                    label: onboardingStages[value].label,
                                    count: matchingRows.filter((row) => row.original.stage === value).length,
                                })),
                            ];
                            return (
                                <div role="group" aria-label="Onboarding stage" className="flex w-full flex-wrap gap-2">
                                    {stageFilters.map((filter) => (
                                        <Button
                                            key={filter.value}
                                            variant={stage === filter.value ? 'highlighted' : 'outline2'}
                                            size="xs"
                                            aria-pressed={stage === filter.value}
                                            onClick={() => {
                                                setSearchParams(
                                                    (params) => {
                                                        if (filter.value === 'all') params.delete('stage');
                                                        else params.set('stage', filter.value);
                                                        return params;
                                                    },
                                                    { replace: true },
                                                );
                                                table.setPageIndex(0);
                                            }}
                                        >
                                            {filter.label}
                                            <span className="tabular-nums">{filter.count}</span>
                                        </Button>
                                    ))}
                                </div>
                            );
                        }}
                        exportName="onboarding-households"
                        cardClassName="m-0 border-0 shadow-none [&>div.border-b]:border-b-0"
                        rowOnClick={(row) => navigate(`/onboarding/${row.globalAccountGUID}`)}
                    />
                )}
            </PageContent>
            {creating && (
                <CreateOnboardingHouseholdSheet
                    onClose={() => setCreating(false)}
                    onSuccess={(guid) => navigate(`/onboarding/${guid}`)}
                />
            )}
        </Page>
    );
};

const OnboardingActions = ({ household }: { household: CommandCenterHousehold }) => {
    const personOrgGUID = household.primaryManagerPersonOrgGUID;
    const { data: inviteDetails, isError } = useInviteDetails(personOrgGUID);
    const invite = useInvitePerson(household.tenantGUID);

    return (
        <div className="flex items-center gap-2" onClick={(event) => event.stopPropagation()}>
            {isError && <span className="text-muted-foreground text-sm">Invite unavailable</span>}
            {!isError && personOrgGUID && inviteDetails?.inviteStatus === InviteStatus.NOT_INVITED && (
                <ButtonWithConfirmModal
                    buttonProps={{
                        children: 'INVITE',
                        size: 'small',
                        buttonType: 'secondary',
                        waiting: invite.isPending,
                        disabled: !household.primaryManagerEmailAddressPlain,
                    }}
                    modalTitle={`Invite ${getPrimaryManagerName(household)}`}
                    onConfirm={() => invite.mutate(personOrgGUID)}
                >
                    <ModalText>
                        Send an invitation email to <b>{household.primaryManagerEmailAddressPlain}</b>?
                    </ModalText>
                </ButtonWithConfirmModal>
            )}
            <AdvisorLoginButton
                globalAccountGUID={household.globalAccountGUID}
                redirect="/overview"
                className="ml-auto"
                variant="ghost"
                size="icon"
            />
        </div>
    );
};
